import {
  Box,
  IconButton,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import BackIcon from '@mui/icons-material/KeyboardArrowLeft';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback } from 'react';
import {
  METRICS_EXPAND_MESSAGING_VIEW,
  useAnalytics,
} from '@xolv-mybrightlink/common';

const CareTeamInfoQuestion = (props: {
  questionKey: string;
  answerKey: string;
}) => {
  return (
    <>
      <CareTeamInfoItem messageKey={props.questionKey} type='question' />
      <CareTeamInfoItem messageKey={props.answerKey} type='answer' />
    </>
  );
};

const CareTeamInfoItem = (props: {
  messageKey: string;
  type: 'question' | 'answer';
}) => {
  const { messageKey, type } = props;
  const leadingColor = type === 'question' ? '#ff6e73' : '#b1b5bd';
  const backgroundColor =
    type === 'question' ? 'rgba(214, 229, 249, 0.5)' : 'white';
  const leading = type === 'question' ? 'Q.' : 'A.';
  return (
    <ListItem sx={{ backgroundColor, padding: 0 }}>
      <ListItemAvatar sx={{ alignSelf: 'flex-start' }}>
        <Typography
          sx={{
            fontSize: 34,
            fontWeight: 600,
            color: leadingColor,
            mx: 5,
            my: 0,
          }}
          paragraph={true}
        >
          {leading}
        </Typography>
      </ListItemAvatar>
      <ListItemText sx={{ textAlign: 'start', mt: 4, alignSelf: 'flex-start' }}>
        <Typography paragraph={true}>
          <FormattedMessage
            id={messageKey}
            values={{
              phone: (phone) => (
                <Link href={`tel:${phone}`} underline='none'>
                  {phone}
                </Link>
              ),
              email: (email) => (
                <Link href={`mailto:${email}`} underline='none'>
                  {email}
                </Link>
              ),
            }}
          />
        </Typography>
      </ListItemText>
    </ListItem>
  );
};

const CareTeamInfoCollapseHeader = (props: {
  onClose: () => void;
  onExpand: () => void;
}) => {
  const analytics = useAnalytics();
  const onExpand = useCallback(() => {
    analytics.trackAction(METRICS_EXPAND_MESSAGING_VIEW);
    props.onExpand();
  }, [analytics, props]);
  return (
    <Box display='flex' sx={{ alignItems: 'center', mr: 5 }}>
      <IconButton onClick={props.onClose}>
        <BackIcon color='primary' sx={{ width: 32, height: 32 }} />
      </IconButton>
      <Typography
        sx={{
          flex: 1,
          lineHeight: '32px',
          ml: 1.25,
          fontSize: 20,
          fontWeight: 500,
        }}
      >
        <FormattedMessage id='messages.info' />
      </Typography>
      <IconButton onClick={onExpand}>
        <img src='/public/assets/icons/ic_expand_care_team.svg' alt='Expand' />
      </IconButton>
    </Box>
  );
};

const CareTeamInfoExpandHeader = (props: { onClose: () => void }) => {
  return (
    <Box display='flex' sx={{ height: 70, alignItems: 'center' }}>
      <Typography
        sx={{
          flex: 1,
          lineHeight: '32px',
          ml: 9,
          fontSize: 20,
          fontWeight: 500,
        }}
      >
        <FormattedMessage id='messages.info' />
      </Typography>
      <IconButton onClick={props.onClose} sx={{ mr: 5 }}>
        <CloseIcon sx={{ width: 32, height: 32, color: '#3b4559' }} />
      </IconButton>
    </Box>
  );
};

const CareTeamInfo = (props: { header: React.ReactNode }) => {
  const intl = useIntl();
  const messages = intl.messages;
  const questionIds = Object.keys(messages).reduce((result, messageId) => {
    const questionId = messageId.match(
      /(?<question>care-team.info.question-\d).question/,
    )?.groups?.['question'];
    if (questionId) {
      return [...result, questionId];
    }
    return result;
  }, [] as string[]);

  return (
    <Box sx={{ bgcolor: 'white' }}>
      {props.header}
      {questionIds.map((questionId) => (
        <CareTeamInfoQuestion
          key={questionId}
          questionKey={`${questionId}.question`}
          answerKey={`${questionId}.answer`}
        />
      ))}
      <Box sx={{ pl: 9, pb: 9 }}>
        <Typography
          sx={{ fontSize: 24, fontWeight: 600, color: '#3b4559', pb: 2.5 }}
        >
          <FormattedMessage id='care-team.info.important' />
        </Typography>
        <Typography>
          <FormattedMessage id='care-team.info.important-content' />
        </Typography>
      </Box>
    </Box>
  );
};

export const CareTeamInfoCollapsed = (props: {
  onClose: () => void;
  onExpand: () => void;
}) => {
  return (
    <CareTeamInfo
      header={
        <CareTeamInfoCollapseHeader
          onClose={props.onClose}
          onExpand={props.onExpand}
        />
      }
    />
  );
};

export const CareTeamInfoExpanded = (props: { onClose: () => void }) => {
  return (
    <CareTeamInfo
      header={<CareTeamInfoExpandHeader onClose={props.onClose} />}
    />
  );
};
